(() => {
  const shuffle = (items) => {
    const result = items.slice();
    for (let index = result.length - 1; index > 0; index -= 1) {
      const other = Math.floor(Math.random() * (index + 1));
      [result[index], result[other]] = [result[other], result[index]];
    }
    return result;
  };
  const family = (product) => {
    const type = product.type.toLowerCase();
    if (/hat|headband|footwear|sock|neck gaiter|bandana/.test(type)) return 'headwear-footwear';
    if (/wall art|poster|flag|blanket|pillow|towel|shower curtain|puzzle|notebook|novelty sign|skateboard/.test(type)) return 'home-art';
    return product.apparel ? 'apparel' : 'accessories';
  };
  const selectProducts = (products, balanced, count = 4) => {
    const unique = Array.from(new Map(products.map((product) => [String(product.id), product])).values());
    const pool = shuffle(unique);
    if (!balanced) return pool.slice(0, count);
    const groups = new Map();
    pool.forEach((product) => {
      const key = family(product);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(product);
    });
    const chosen = [];
    const usedTypes = new Set();
    shuffle(Array.from(groups.values())).forEach((group) => {
      if (chosen.length >= count) return;
      const product = group.find((item) => !usedTypes.has(item.type)) || group[0];
      chosen.push(product);
      usedTypes.add(product.type);
    });
    const chosenIds = new Set(chosen.map((product) => String(product.id)));
    const remaining = pool.filter((product) => !chosenIds.has(String(product.id)));
    remaining.sort((a, b) => Number(usedTypes.has(a.type)) - Number(usedTypes.has(b.type)));
    return shuffle(chosen.concat(remaining.slice(0, count - chosen.length)));
  };
  const imageUrl = (source, width) => {
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.href;
  };
  class SilberhazeMerchSlider extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.index = 0;
      this.userPaused = false;
      this.interacted = false;
      this.hovered = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.strings = JSON.parse(this.querySelector('[data-slider-strings]').textContent);
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.container = this.querySelector('.sh-merch__slides');
      this.controls = this.querySelector('[data-controls]');
      this.status = this.querySelector('[data-status]');
      this.listen(this.querySelector('[data-previous]'), 'click', () => this.go(this.index - 1, true));
      this.listen(this.querySelector('[data-next]'), 'click', () => this.go(this.index + 1, true));
      this.listen(this, 'keydown', (event) => {
        if (/INPUT|TEXTAREA|SELECT/.test(event.target.tagName) || event.target.isContentEditable) return;
        const actions = {ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1};
        if (Object.hasOwn(actions, event.key)) {
          event.preventDefault();
          this.go(actions[event.key], true);
        }
      });
      this.listen(this, 'pointerenter', (event) => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.updateTimer(); }
      });
      this.listen(this, 'pointerleave', () => { this.hovered = false; this.updateTimer(); });
      this.listen(this, 'focusin', () => { this.interacted = true; this.updateTimer(); });
      this.listen(this, 'focusout', () => window.setTimeout(() => this.updateTimer(), 0));
      this.listen(document, 'visibilitychange', () => this.updateTimer());
      this.listen(this.reducedMotion, 'change', () => this.updateTimer());
      this.listen(this.querySelector('[data-play]'), 'click', () => {
        this.userPaused = !this.userPaused;
        this.interacted = true;
        this.updateTimer();
      });
      this.listen(this.container, 'touchstart', (event) => {
        if (event.touches.length === 1) this.touchStart = {x: event.touches[0].clientX, y: event.touches[0].clientY};
      }, {passive: true});
      this.listen(this.container, 'touchend', (event) => {
        if (!this.touchStart || !event.changedTouches.length) return;
        const deltaX = event.changedTouches[0].clientX - this.touchStart.x;
        const deltaY = event.changedTouches[0].clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(deltaX) > 55 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
          this.suppressClickUntil = performance.now() + 350;
          this.go(this.index + (deltaX < 0 ? 1 : -1), true);
        }
      }, {passive: true});
      this.listen(this.container, 'click', (event) => {
        if (performance.now() < (this.suppressClickUntil || 0)) event.preventDefault();
      });
      this.makeDots();
      this.show(false);
      this.controls.hidden = this.slides.length < 2;
      if (this.dataset.randomize === 'true') this.randomize();
      else this.updateTimer();
    }
    disconnectedCallback() {
      this.abort?.abort();
      clearInterval(this.timer);
      this.initialized = false;
    }
    listen(target, event, handler, options = {}) {
      target?.addEventListener(event, handler, {...options, signal: this.abort.signal});
    }
    label(template, index) {
      return template.replace('[index]', String(index + 1)).replace('[count]', String(this.slides.length));
    }
    makeDots() {
      const dots = this.querySelector('[data-dots]');
      dots.replaceChildren();
      this.slides.forEach((slide, index) => {
        slide.setAttribute('aria-label', this.label(this.strings.position, index));
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sh-merch__dot';
        button.setAttribute('aria-label', this.label(this.strings.show_slide, index));
        button.setAttribute('aria-controls', this.container.id);
        this.listen(button, 'click', () => this.go(index, true));
        dots.append(button);
      });
    }
    go(index, manual) {
      if (!this.slides.length) return;
      if (manual) this.interacted = true;
      this.index = (index + this.slides.length) % this.slides.length;
      this.show(manual);
      this.updateTimer();
    }
    show(announce) {
      this.slides.forEach((slide, index) => { slide.hidden = index !== this.index; });
      this.querySelectorAll('.sh-merch__dot').forEach((button, index) => {
        button.setAttribute('aria-current', index === this.index ? 'true' : 'false');
      });
      const current = this.slides[this.index];
      const image = current?.querySelector('img');
      if (image) image.loading = 'eager';
      if (announce && current) this.status.textContent = this.label(this.strings.position, this.index) + ': ' + current.querySelector('h2').textContent;
    }
    updateTimer() {
      clearInterval(this.timer);
      const play = this.querySelector('[data-play]');
      if (play) {
        play.textContent = this.userPaused ? this.strings.play : this.strings.pause;
        play.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
      }
      if (!this.isConnected || this.dataset.autoplay !== 'true' || this.userPaused || this.hovered || this.matches(':focus-within') || document.hidden || this.reducedMotion.matches || this.slides.length < 2) return;
      this.timer = window.setInterval(() => this.go(this.index + 1, false), Number(this.dataset.interval) || 7000);
    }
    async catalog() {
      const initialElement = this.querySelector('[data-merch-catalog]');
      if (!initialElement) throw new Error('Missing merchandise metadata');
      const initial = JSON.parse(initialElement.textContent);
      const pages = [initial];
      const totalPages = Number(initial.pages);
      if (!Number.isInteger(totalPages) || totalPages < 1 || totalPages > 100) throw new Error('Invalid merchandise pagination');
      const pending = Array.from({length: totalPages}, (_, index) => index + 1).filter((page) => page !== initial.page);
      for (let index = 0; index < pending.length; index += 3) {
        const batch = await Promise.all(pending.slice(index, index + 3).map(async (page) => {
          const url = new URL(this.dataset.catalogUrl, window.location.origin);
          if (url.origin !== window.location.origin) throw new Error('Invalid catalog origin');
          url.searchParams.set('section_id', 'sh-merch-catalog');
          url.searchParams.set(initial.page_param || 'page', String(page));
          const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          const controller = new AbortController();
          const timeout = window.setTimeout(() => controller.abort(), 6000);
          const onAbort = () => controller.abort();
          this.abort.signal.addEventListener('abort', onAbort, {once: true});
          try {
            const response = await fetch(url.href, {credentials: 'same-origin', signal: controller.signal, headers: {'Accept': 'text/html'}});
            if (!response.ok) throw new Error('Merchandise metadata unavailable');
            const fragment = new DOMParser().parseFromString(await response.text(), 'text/html');
            const element = fragment.querySelector('[data-merch-catalog]');
            if (!element) throw new Error('Missing merchandise page');
            const data = JSON.parse(element.textContent);
            if (data.page !== page || data.pages !== totalPages) throw new Error('Incomplete merchandise page');
            return data;
          } finally {
            window.clearTimeout(timeout);
            this.abort.signal.removeEventListener('abort', onAbort);
          }
        }));
        pages.push(...batch);
      }
      return pages.flatMap((page) => page.products).filter((product) => {
        if (!product.id || !product.title || !product.type || !product.image || !product.url) return false;
        const url = new URL(product.url, window.location.origin);
        const image = new URL(product.image, window.location.origin);
        return url.origin === window.location.origin && /\/products\/[^/]+/.test(url.pathname) && (image.origin === window.location.origin || image.hostname === 'cdn.shopify.com') && /^https?:$/.test(image.protocol);
      });
    }
    buildSlide(product) {
      const slide = document.createElement('article');
      slide.className = 'sh-merch__slide';
      slide.dataset.slide = '';
      slide.dataset.productId = String(product.id);
      slide.dataset.productType = product.type;
      slide.dataset.productFamily = family(product);
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', this.slides[0]?.getAttribute('aria-roledescription') || 'slide');
      slide.hidden = true;
      const media = document.createElement('a');
      media.className = 'sh-merch__media';
      media.href = product.url;
      media.setAttribute('aria-label', product.title);
      const image = document.createElement('img');
      image.alt = product.title;
      image.width = product.width || 1200;
      image.height = product.height || 1200;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.sizes = '(min-width: 990px) 55vw, (min-width: 750px) 60vw, 100vw';
      image.srcset = [360, 540, 720, 960, 1200].map((width) => imageUrl(product.image, width) + ' ' + width + 'w').join(', ');
      image.src = imageUrl(product.image, 960);
      media.append(image);
      const copy = document.createElement('div');
      copy.className = 'sh-merch__copy';
      const eyebrow = document.createElement('p');
      eyebrow.className = 'sh-merch__eyebrow';
      eyebrow.textContent = this.strings.eyebrow;
      const heading = document.createElement('h2');
      heading.className = 'sh-merch__title';
      const title = document.createElement('a');
      title.href = product.url;
      title.textContent = product.title;
      title.title = product.title;
      heading.append(title);
      const type = document.createElement('p');
      type.className = 'sh-merch__type';
      type.textContent = product.type;
      const link = document.createElement('a');
      link.className = 'sh-merch__button';
      link.href = product.url;
      link.textContent = this.strings.view_product;
      copy.append(eyebrow, heading, type, link);
      slide.append(media, copy);
      return slide;
    }
    async randomize() {
      try {
        const catalog = await this.catalog();
        this.catalogSize = catalog.length;
        this.dataset.catalogSize = String(catalog.length);
        const selected = selectProducts(catalog, this.dataset.balance === 'true');
        if (!selected.length || !this.isConnected || this.interacted) return;
        const slides = selected.map((product) => this.buildSlide(product));
        const firstImage = slides[0].querySelector('img');
        firstImage.loading = 'eager';
        firstImage.fetchPriority = 'high';
        await Promise.race([firstImage.decode(), new Promise((_, reject) => window.setTimeout(() => reject(new Error('Image timeout')), 6000))]);
        if (!this.isConnected || this.interacted) return;
        this.container.replaceChildren(...slides);
        this.slides = slides;
        this.index = 0;
        this.makeDots();
        this.show(false);
        this.controls.hidden = this.slides.length < 2;
        this.dataset.randomized = 'true';
      } catch {
        this.dataset.randomized = 'fallback';
      } finally {
        this.updateTimer();
      }
    }
  }
  if (!customElements.get('silberhaze-merch-slider')) customElements.define('silberhaze-merch-slider', SilberhazeMerchSlider);
})();
